import { useState, useEffect, useRef, Fragment, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronDown, FaChevronRight, FaSignOutAlt } from "react-icons/fa";
import { LuLock } from "react-icons/lu";
import { useTheme } from "../context/SocketContext";
import { useRealtimeRefresh } from "../context/RealtimeSyncContext";
import logo from "../Assets/HD Quality Logo.svg";
import logoDark from "../Assets/HD Quality Logo 2.svg";
import {
  useAccountPortal,
  useLayoutPortal,
  getPortalIcon,
  getAccountSettingsMeta,
  buildAccountNavItems,
  getSidebarContent,
  isServiceNavDisabled,
  buildPortalFileUrl,
} from "../portal";

const Sidebar = ({ sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed }) => {
  const [accountDropdown, setAccountDropdown] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const { isDarkMode, currentThemeColors } = useTheme();
  const { sidebar: sidebarContent } = useLayoutPortal();
  const sidebarConfig = getSidebarContent(sidebarContent);
  const sidebarLogoSrc = isDarkMode
    ? (sidebarConfig.logo?.darkUrl ? buildPortalFileUrl(sidebarConfig.logo.darkUrl) : logoDark)
    : (sidebarConfig.logo?.lightUrl ? buildPortalFileUrl(sidebarConfig.logo.lightUrl) : logo);
  const sidebarLogoAlt = sidebarConfig.logo?.altText || 'BooksComply';
  const ReferIcon = getPortalIcon(sidebarConfig.referAndEarn.icon || 'LuGift');
  const AssigneeIcon = getPortalIcon(sidebarConfig.assigneeNav.icon || 'LuBookOpenCheck');
  const { settings, profile, invoice, paymentHistory } = useAccountPortal();
  const accountSection = getAccountSettingsMeta(settings);
  const accountNavItems = buildAccountNavItems(profile, invoice, paymentHistory);
  const AccountSectionIcon = getPortalIcon(accountSection.icon || 'LuSettings');

  const renderAccountDropdown = (afterIndex) => {
    if (!sidebarCollapsed) {
      return (
        <div className="relative">
          <button
            id="sidebar-account-settings"
            onClick={toggleDropdown}
            className={`sidebar-account-toggle ${accountDropdown ? "open" : ""} ${isAccountSectionActive ? "has-active-child" : ""}`}
          >
            <div className="flex items-center gap-3">
              <AccountSectionIcon className="w-5 h-5" style={{ color: "var(--primary-500)" }} />
              <span className="font-medium">{accountSection.title || 'Account Settings'}</span>
            </div>
            <FaChevronDown className={`w-4 h-4 transition-transform duration-200 ${accountDropdown ? "rotate-180" : ""}`} />
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${accountDropdown ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}>
            <div className="sidebar-submenu">
              {accountNavItems.map((item) => {
                const ItemIcon = getPortalIcon(item.icon || 'FaUser');
                return (
                  <Link
                    key={item.key}
                    id={`sidebar-${item.key}`}
                    to={item.route}
                    onClick={handleLinkClick}
                    className={`sidebar-submenu-link ${isActive(item.route) ? "active" : ""}`}
                  >
                    <ItemIcon className="w-4 h-4" />
                    {item.title}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return accountNavItems.map((item, idx) => {
      const ItemIcon = getPortalIcon(item.icon || 'FaUser');
      return (
        <NavLink
          key={item.key}
          to={item.route}
          icon={ItemIcon}
          index={afterIndex + idx + 1}
          id={`sidebar-${item.key}-collapsed`}
        >
          {item.title}
        </NavLink>
      );
    });
  };

  const refreshSidebarUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch user data.");

      const data = await response.json();
      setUser(data.user);
    } catch (error) {
      console.error("User fetch error:", error);
    }
  }, []);

  useEffect(() => {
    refreshSidebarUser();
  }, [refreshSidebarUser]);

  useRealtimeRefresh("profile", refreshSidebarUser);

  useEffect(() => {
    window.openAccountDropdown = () => setAccountDropdown(true);
    return () => { delete window.openAccountDropdown; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen, setSidebarOpen]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const toggleDropdown = () => {
    setAccountDropdown((prev) => !prev);
  };

  const isActive = (path) => location.pathname === path;

  const accountRoutes = ["/profile", "/invoice", "/payment-history"];
  const isAccountSectionActive = accountRoutes.some((path) => isActive(path));

  useEffect(() => {
    if (isAccountSectionActive) {
      setAccountDropdown(true);
    }
  }, [isAccountSectionActive]);

  const handleLinkClick = () => {
    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
  };

  const SidebarNavLabel = ({ text, active }) => {
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const isLong = typeof text === 'string' && text.length > 18;
    const [isOverflowing, setIsOverflowing] = useState(isLong);

    useEffect(() => {
      const checkOverflow = () => {
        if (containerRef.current && textRef.current) {
          const hasOverflow = textRef.current.scrollWidth > containerRef.current.clientWidth + 4;
          setIsOverflowing(hasOverflow || isLong);
        }
      };
      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }, [text, isLong]);

    return (
      <div
        ref={containerRef}
        className={`flex-1 min-w-0 relative overflow-hidden ${isOverflowing ? 'sidebar-marquee-container' : ''} ${active ? 'pr-4' : ''}`}
        title={typeof text === 'string' ? text : ''}
      >
        {isOverflowing ? (
          <div className="sidebar-marquee-track inline-flex items-center whitespace-nowrap">
            <span ref={textRef} className="font-medium shrink-0 pr-6">
              {text}
            </span>
            <span className="opacity-40 text-xs shrink-0 pr-6">•</span>
            <span className="font-medium shrink-0 pr-6">
              {text}
            </span>
            <span className="opacity-40 text-xs shrink-0 pr-6">•</span>
          </div>
        ) : (
          <span ref={textRef} className="font-medium truncate block">
            {text}
          </span>
        )}
      </div>
    );
  };

  const NavLink = ({ to, icon: Icon, children, disabled = false, index = 0, id }) => {
    if (disabled) {
      return (
        <div
          id={id}
          className="sidebar-link-disabled opacity-0 animate-fade-in-up"
          style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
          title={sidebarCollapsed ? children : ''}
        >
          <LuLock className="w-5 h-5 text-gray-400 dark:text-dark-500 shrink-0" />
          {!sidebarCollapsed && <SidebarNavLabel text={children} active={false} />}
        </div>
      );
    }

    return (
      <Link
        id={id}
        to={to}
        onClick={handleLinkClick}
        className={`sidebar-link group ${isActive(to) ? 'active' : ''} opacity-0 animate-fade-in-up`}
        style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
        title={sidebarCollapsed ? children : ''}
      >
        <Icon
          className={`w-5 h-5 shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${isActive(to) ? 'text-white' : ''}`}
          style={{ color: isActive(to) ? 'white' : 'var(--primary-500)' }}
        />
        {!sidebarCollapsed && <SidebarNavLabel text={children} active={isActive(to)} />}
        {isActive(to) && !sidebarCollapsed && (
          <div className="absolute right-3 w-2 h-2 bg-white rounded-full animate-ping-slow shrink-0" />
        )}
      </Link>
    );
  };

  return (
    <aside
      ref={sidebarRef}
      className={`
        fixed top-0 left-0 z-50 h-screen
        bg-white dark:bg-dark-800
        border-r-2 border-gray-200 dark:border-dark-700
        transform transition-all duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'lg:w-20 w-72' : 'w-72'}
        flex flex-col
        shadow-xl lg:shadow-none
      `}
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700 gap-2">
        <div className={`flex-1 flex items-center ${sidebarCollapsed ? 'justify-start' : 'pr-6'}`}>
          {!sidebarCollapsed && (
            <img
              src={sidebarLogoSrc}
              alt={sidebarLogoAlt}
              className="h-10 w-auto max-w-[250px] object-contain mr-10"
            />
          )}
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
        >
          <FaChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:block p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <FaChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          ) : (
            <FaChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          )}
        </button>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto py-4 px-3 scrollbar-hide">
        {(user?.isAssignee || user?.isBookkeepingCollaborator) ? (
          <nav className="space-y-1">
            <NavLink to={sidebarConfig.assigneeNav.route} icon={AssigneeIcon} index={0} id="sidebar-bookkeeping">
              {sidebarConfig.assigneeNav.label}
            </NavLink>
          </nav>
        ) : (
          <>
            <nav className="space-y-1">
              {sidebarConfig.mainNav.map((item, index) => (
                <Fragment key={item.key}>
                  <NavLink
                    to={item.route}
                    icon={getPortalIcon(item.icon)}
                    index={index}
                    id={`sidebar-${item.key}`}
                  >
                    {item.label}
                  </NavLink>
                  {item.key === 'help' && renderAccountDropdown(index)}
                </Fragment>
              ))}
            </nav>

            <div className="mt-6">
              {!sidebarCollapsed && (
                <div id="sidebar-services-header">
                  <div className="px-4 mb-3">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 dark:text-dark-400">
                      {sidebarConfig.servicesSectionTitle}
                    </h3>
                  </div>
                  <div className="h-px mb-3" style={{ background: "linear-gradient(to right, var(--primary-500), transparent)" }} />
                </div>
              )}

              <nav className="space-y-1">
                {sidebarConfig.serviceNav.map((item, index) => (
                  <NavLink
                    key={item.key}
                    to={item.route}
                    icon={item.key === 'tax-filing' ? getPortalIcon('LuClipboardCheck') : getPortalIcon(item.icon)}
                    disabled={isServiceNavDisabled(item.key, user)}
                    index={sidebarConfig.mainNav.length + index + 3}
                    id={`sidebar-${item.key}`}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </>
        )}
      </div>

      {!(user?.isAssignee || user?.isBookkeepingCollaborator) && sidebarConfig.referAndEarn.isActive !== false && (
        <div className="px-3 pb-2 border-t border-gray-100 dark:border-dark-700 pt-3">
          <Link
            to={sidebarConfig.referAndEarn.route || '/refer-and-earn'}
            onClick={handleLinkClick}
            className={`group relative flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-4 py-3.5 rounded-xl overflow-hidden transition-all duration-300 ${isActive(sidebarConfig.referAndEarn.route || '/refer-and-earn')
              ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/40'
              : 'bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-pink-500/10 dark:from-amber-500/20 dark:via-orange-500/20 dark:to-pink-500/20 text-amber-700 dark:text-amber-400 hover:from-amber-500 hover:via-orange-500 hover:to-pink-500 hover:text-white hover:shadow-lg hover:shadow-orange-500/30'
              }`}
            title={sidebarCollapsed ? sidebarConfig.referAndEarn.title : ''}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <div className={`relative p-2 rounded-lg transition-all duration-300 ${isActive(sidebarConfig.referAndEarn.route || '/refer-and-earn')
              ? 'bg-white/20'
              : 'bg-gradient-to-br from-amber-500 to-orange-500 group-hover:bg-white/20'
              }`}>
              <ReferIcon className={`w-5 h-5 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 ${isActive(sidebarConfig.referAndEarn.route || '/refer-and-earn') ? 'text-white' : 'text-white group-hover:text-white'
                }`} />
            </div>
            {!sidebarCollapsed && (
              <>
                <div className="flex flex-col">
                  <span className="font-bold text-base">{sidebarConfig.referAndEarn.title}</span>
                  <span className={`text-sm transition-colors ${isActive(sidebarConfig.referAndEarn.route || '/refer-and-earn')
                    ? 'text-white/80'
                    : 'text-amber-600/70 dark:text-amber-400/70 group-hover:text-white/80'
                    }`}>
                    {sidebarConfig.referAndEarn.subtitle}
                  </span>
                </div>
                <FaChevronRight className={`w-4 h-4 ml-auto transition-transform duration-300 group-hover:translate-x-1 ${isActive(sidebarConfig.referAndEarn.route || '/refer-and-earn') ? 'text-white' : 'text-amber-500 group-hover:text-white'
                  }`} />
              </>
            )}
            {isActive(sidebarConfig.referAndEarn.route || '/refer-and-earn') && !sidebarCollapsed && (
              <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full animate-pulse" />
            )}
          </Link>
        </div>
      )}

      {/* Logout Button - Mobile Only */}
      <div className="p-4 border-t border-gray-100 dark:border-dark-700 lg:hidden">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl
            bg-gradient-to-r from-red-500 to-red-600
            text-white font-semibold text-base
            shadow-lg shadow-red-500/30
            hover:shadow-xl hover:shadow-red-500/40
            hover:from-red-600 hover:to-red-700
            active:scale-[0.98]
            transition-all duration-200"
        >
          <FaSignOutAlt className="w-5 h-5" />
          Logout
        </button>
      </div>

      {/* 3D Decorative Element */}
      <div
        className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-3xl pointer-events-none animate-pulse-slow"
        style={{ background: `linear-gradient(135deg, ${currentThemeColors?.primary}20, ${currentThemeColors?.accent}20)` }}
      />
    </aside>
  );
};

export default Sidebar;